// Register a lightweight slide viewer editor for Serenity forms.
// Expects the editor value to be either:
//  - comma separated filenames/paths, or
//  - JSON array of strings.
// It will render <img src="/ImageFullsize/{image}" style="width:100%;height:100%"/>.
//
// Location: Modules/Common/Fields/ImageSlideEditor.ts
// Make sure this file is included in your build bundling (same place as other editors).

import { Widget } from "@serenity-is/corelib";
export class ImageSlideEditor extends Widget<any> {
    // register editor for Serenity 9 style
    static override[Symbol.typeInfo] = this.registerEditor('VinaSAB.Common.Fields.ImageSlideEditor');

    private images: string[] = [];
    private current = 0;
    // keep as `any` because Serenity 9 may pass different container shapes
    private root: any;
    private imgEl!: HTMLImageElement;
    private idxEl!: HTMLElement;
    private prevBtn!: HTMLButtonElement;
    private nextBtn!: HTMLButtonElement;
    private wrapperEl!: HTMLElement;
    private labelEl?: HTMLElement | null;

    // Serenity 9 no-jQuery: accept HTMLElement or framework container
    constructor(container: any) {
        super(container);
        console.log('ImageSlideEditor.constructor', container);
        this.root = container;
        this.buildUi();
    }

    private buildUi(): void {
        // Create wrapper first
        const wrapper = document.createElement('div');
        wrapper.className = 'image-slide-editor-wrapper';
        wrapper.style.position = 'relative';
        wrapper.style.width = '100%';
        wrapper.style.height = '240px';
        wrapper.style.background = '#f5f5f5';
        wrapper.style.display = 'flex';
        wrapper.style.alignItems = 'center';
        wrapper.style.justifyContent = 'center';
        wrapper.style.overflow = 'hidden';
        wrapper.style.borderRadius = '4px';

        // image element
        this.imgEl = document.createElement('img');
        this.imgEl.style.width = '100%';
        this.imgEl.style.height = '100%';
        this.imgEl.style.objectFit = 'contain';
        this.imgEl.alt = 'image';
        wrapper.appendChild(this.imgEl);

        // index label (bottom-left)
        this.idxEl = document.createElement('div');
        this.idxEl.style.position = 'absolute';
        this.idxEl.style.left = '8px';
        this.idxEl.style.bottom = '8px';
        this.idxEl.style.padding = '4px 8px';
        this.idxEl.style.background = 'rgba(0,0,0,0.6)';
        this.idxEl.style.color = 'white';
        this.idxEl.style.fontSize = '12px';
        this.idxEl.style.borderRadius = '3px';
        wrapper.appendChild(this.idxEl);

        // prev / next buttons
        this.prevBtn = document.createElement('button');
        this.prevBtn.type = 'button';
        this.prevBtn.className = 'btn btn-sm btn-light';
        this.prevBtn.style.position = 'absolute';
        this.prevBtn.style.left = '8px';
        this.prevBtn.style.top = '50%';
        this.prevBtn.style.transform = 'translateY(-50%)';
        this.prevBtn.textContent = '‹';
        this.prevBtn.onclick = () => { this.prev(); };
        wrapper.appendChild(this.prevBtn);

        this.nextBtn = document.createElement('button');
        this.nextBtn.type = 'button';
        this.nextBtn.className = 'btn btn-sm btn-light';
        this.nextBtn.style.position = 'absolute';
        this.nextBtn.style.right = '8px';
        this.nextBtn.style.top = '50%';
        this.nextBtn.style.transform = 'translateY(-50%)';
        this.nextBtn.textContent = '›';
        this.nextBtn.onclick = () => { this.next(); };
        wrapper.appendChild(this.nextBtn);

        // Determine the actual host element to append into.
        let hostEl: HTMLElement | null = null;

        try {
            if (this.root instanceof HTMLElement) {
                hostEl = this.root;
            }
            else if (this.root && typeof this.root.element === 'object' && this.root.element instanceof HTMLElement) {
                hostEl = this.root.element;
            }
            else if (this.root && typeof this.root.get === 'function') {
                const el = this.root.get(0);
                if (el instanceof HTMLElement) hostEl = el;
            }
            else if (this.root && typeof this.root.append === 'function') {
                // will try append later
            }
            else if (this.root && typeof this.root.id === 'string') {
                hostEl = document.getElementById(this.root.id);
            }
            else if (typeof this.root === 'string') {
                hostEl = document.getElementById(this.root);
            }
        } catch (e) {
            console.warn('ImageSlideEditor: host detection error', e);
        }

        // Append wrapper robustly
        if (hostEl) {
            hostEl.appendChild(wrapper);
            // find label within .field ancestor and store it
            this.labelEl = this.findLabelForHost(hostEl);
        }
        else if (this.root && typeof this.root.append === 'function') {
            try {
                this.root.append(wrapper);
                // try to locate label using root.id if exists
                if (this.root && typeof this.root.id === 'string') {
                    const el = document.getElementById(this.root.id);
                    if (el) this.labelEl = this.findLabelForHost(el);
                }
            } catch {
                if (this.root && typeof this.root.id === 'string') {
                    const el = document.getElementById(this.root.id);
                    if (el) {
                        el.appendChild(wrapper);
                        this.labelEl = this.findLabelForHost(el);
                    }
                }
            }
        }
        else if (this.root && typeof this.root.get === 'function') {
            const el = this.root.get(0) as HTMLElement;
            if (el && typeof el.appendChild === 'function') {
                el.appendChild(wrapper);
                this.labelEl = this.findLabelForHost(el);
            }
        }
        else if (this.root && typeof this.root.id === 'string') {
            const el = document.getElementById(this.root.id);
            if (el) {
                el.appendChild(wrapper);
                this.labelEl = this.findLabelForHost(el);
            }
        }
        else {
            try {
                const inferredId = (this.root && (this.root.id || this.root.name)) || null;
                if (inferredId) {
                    const el = document.getElementById(inferredId);
                    if (el) {
                        el.appendChild(wrapper);
                        this.labelEl = this.findLabelForHost(el);
                    }
                    else {
                        document.body.appendChild(wrapper);
                        console.warn('ImageSlideEditor: appended to document.body as fallback; container not found', this.root);
                    }
                } else {
                    document.body.appendChild(wrapper);
                    console.warn('ImageSlideEditor: appended to document.body as fallback; container not found', this.root);
                }
            } catch {
                // keep silent
            }
        }

        // keep wrapper reference
        this.wrapperEl = wrapper;
        this.updateUi();
    }

    private findLabelForHost(host: HTMLElement): HTMLElement | null {
        try {
            let el: HTMLElement | null = host;
            while (el) {
                if (el.classList && el.classList.contains('field')) {
                    const label = el.querySelector('label.caption, label') as HTMLElement | null;
                    return label;
                }
                el = el.parentElement;
            }
        } catch { /* ignore */ }
        return null;
    }

    // parse incoming value (string/array) into images[]
    private parseValue(v: any): string[] {
        if (v == null) return [];
        if (Array.isArray(v)) return v.map(x => String(x).trim()).filter(x => x.length > 0);

        let s = String(v).trim();
        if (!s) return [];

        // try JSON array first
        if ((s.startsWith('[') && s.endsWith(']'))) {
            try {
                const arr = JSON.parse(s);
                if (Array.isArray(arr)) return arr.map(x => String(x).trim()).filter(x => x.length > 0);
            } catch { /* fall back */ }
        }

        // fallback: comma separated
        return s.split(',').map(x => x.trim()).filter(x => x.length > 0);
    }

    // Property-style API kept for convenience (some code may set .value directly)
    public set value(v: any) {
        this.images = this.parseValue(v);
        this.current = 0;
        this.updateUi();
    }
    public get value(): any {
        return this.images.length === 0 ? '' : JSON.stringify(this.images);
    }

    // Serenity editor API compatibility:
    // Serenity typically calls set_value / get_value methods on editors.
    // Provide plain methods so framework will update this editor.
    public set_value(v: any): void {
        console.log('ImageSlideEditor.set_value', v);
        this.value = v;
    }

    public get_value(): any {
        return this.value;
    }

    private updateUi(): void {
        if (!this.imgEl || !this.wrapperEl) return;

        // If there are no images hide the whole control (wrapper) and hide label
        if (this.images.length === 0) {
            this.wrapperEl.style.display = 'none';
            if (this.labelEl) this.labelEl.style.display = 'none';
            this.imgEl.src = '';
            this.idxEl.textContent = '0/0';
            this.prevBtn.style.display = 'none';
            this.nextBtn.style.display = 'none';
            return;
        }

        // show wrapper and render current image and label
        this.wrapperEl.style.display = 'flex';
        if (this.labelEl) this.labelEl.style.display = '';

        const img = this.images[Math.max(0, Math.min(this.current, this.images.length - 1))];
        this.imgEl.src = '/ImageFullsize/' + encodeURIComponent(img);
        this.idxEleText(this.current + 1, this.images.length);
        this.prevBtn.style.display = this.images.length > 1 ? '' : 'none';
        this.nextBtn.style.display = this.images.length > 1 ? '' : 'none';
    }

    private idxEleText(current: number, total: number) {
        if (this.idxEl) this.idxEl.textContent = `${current}/${total}`;
    }

    private prev(): void {
        if (this.images.length <= 1) return;
        this.current = (this.current - 1 + this.images.length) % this.images.length;
        this.updateUi();
    }

    private next(): void {
        if (this.images.length <= 1) return;
        this.current = (this.current + 1) % this.images.length;
        this.updateUi();
    }
}
